import { Box, Carousel, CarouselItem, Tooltip } from "@prismicio/editor-ui"
import {
  type ImageContent,
  type ImageContentView,
  isImageContent
} from "@prismicio/types-internal/lib/content"

import {
  type CropperImage,
  getCarouselItemSource,
  getPreviewImage,
  type ImageBlockData,
  updateImageContentAlt
} from "../../../core/imageUtils"
import { type CroppedImages } from "../CropperDialog"
import { ImageAltControl } from "./ImageAltControl"
import { ImageFieldHeader } from "./ImageFieldHeader"
import { ImageLinkControl } from "./ImageLinkControl"

interface NonEmptyCardContentProps<
  ContentType extends ImageContent | ImageBlockData
> {
  allContentViews: readonly (ImageContentView | undefined)[]
  content: ContentType
  contentView: ImageContentView | undefined
  fieldLabel: string
  id: string
  imageLabel: string[]
  initialImages: CropperImage[]
  isUpdating: boolean
  page?: number
  readOnly: boolean
  thumbnailName: string
  onClear: () => void
  onContentChange: (content: ContentType | undefined) => void
  onCropperDialogClose: (result: {
    croppedImages: CroppedImages
    option: number
  }) => void
  onPageChange?: (page: number) => void
  onMediaDialogOpenChange: (open: boolean) => void
}

export function NonEmptyCardContent<
  ContentType extends ImageContent | ImageBlockData
>(props: NonEmptyCardContentProps<ContentType>) {
  const {
    allContentViews,
    content,
    contentView,
    fieldLabel,
    id,
    imageLabel,
    initialImages,
    isUpdating,
    page = 0,
    readOnly,
    thumbnailName,
    onClear,
    onContentChange,
    onCropperDialogClose,
    onPageChange = () => {},
    onMediaDialogOpenChange
  } = props
  const isSVGContentView = content?.url?.split("?")[0]?.slice(-4) === ".svg"

  const visibility = content?.url && !readOnly ? "visible" : "hidden"

  const isRichText = !isImageContent(content)

  return (
    <Box display="flex" gap={12} padding={{ inline: 12 }}>
      <Box>
        <Carousel
          getItemSource={getCarouselItemSource}
          items={allContentViews}
          onPageChange={onPageChange}
          page={page}
          buttonSize="small"
          renderPrevTooltip={(trigger) => (
            <Tooltip content="Previous">{trigger}</Tooltip>
          )}
          renderNextTooltip={(trigger) => (
            <Tooltip content="Next">{trigger}</Tooltip>
          )}
          style={
            isRichText
              ? { width: 181, height: 142 }
              : { width: 123, height: 93 }
          }
        >
          {(contentView: ImageContentView | undefined) =>
            contentView?.url && (
              <CarouselItem
                aria-label="Image Field Item"
                src={getPreviewImage(contentView.url)}
                alt={contentView.alt ?? undefined}
                key={`${thumbnailName}_${contentView.url}`}
                isUpdating={isUpdating}
                disableAnimation={isRichText}
              />
            )
          }
        </Carousel>
      </Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        gap={isRichText ? 16 : 12}
      >
        <ImageFieldHeader
          fieldLabel={fieldLabel}
          imageLabel={imageLabel}
          initialImages={initialImages}
          page={page}
          displaySeparator={!isRichText}
          isSVGContentView={isSVGContentView}
          visibility={visibility}
          onClear={onClear}
          onCropperDialogClose={onCropperDialogClose}
          onMediaDialogOpenChange={onMediaDialogOpenChange}
        />
        <ImageAltControl
          id={id}
          readOnly={readOnly || !contentView}
          altText={contentView?.alt ?? ""}
          onValueChange={(value) => {
            if (contentView) {
              onContentChange(
                updateImageContentAlt(
                  thumbnailName,
                  content,
                  contentView,
                  value
                )
              )
            }
          }}
        />
        {isRichText && (
          <ImageLinkControl
            linkTo={content.linkTo}
            onLinkToChange={(linkTo) => {
              onContentChange({ ...content, linkTo })
            }}
          />
        )}
      </Box>
    </Box>
  )
}
